import { Button, Icon, Tag, Text } from '@pm/ui';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import type { ParseKeys } from 'i18next';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { DetailList } from '../DetailList/DetailList';

const card = cva('rounded-m p-m', {
  variants: {
    variant: {
      primary: 'bg-accent-strong',
      secondary: 'bg-surface border border-default',
    },
  },
});

/**
 * The focus ring is driven only by the link overlay: `focus-within` would also
 * light up for the CTA button, which has a ring of its own.
 */
const cardWrapper = (args: { variant: ConditionCardVariant }) =>
  clsx(card(args), 'has-[[data-card-link]:focus-visible]:ring');

export type ConditionCardVariant = 'primary' | 'secondary';

export enum ConditionCardMode {
  New = 'new',
  OngoingPrescription = 'ongoingPrescription',
  OngoingStatus = 'ongoingStatus',
}

export enum ConditionCardActionState {
  StartRenewal = 'startRenewal',
  UpdateStatus = 'updateStatus',
  RenewalInProgress = 'renewalInProgress',
  RenewalSubmitted = 'renewalSubmitted',
}

export enum ConditionCardStatus {
  RequestSubmitted = 'requestSubmitted',
}

export type ConditionCardBaseProps = {
  /**
   * `primary` is the condition the patient is with us for, and takes the brand's
   * strong accent surface. Everything else is `secondary`.
   */
  variant: ConditionCardVariant;
  title: string;
  icon: ReactNode;
  headingLevel?: 'h2' | 'h3';
};

/**
 * The two live actions carry a handler; the two the patient is waiting on are
 * always disabled and have nothing to call.
 */
export type ConditionCardAction =
  | {
      state:
        | ConditionCardActionState.StartRenewal
        | ConditionCardActionState.UpdateStatus;
      onAction: () => void;
      /** Set when something outside the card makes the action unavailable. */
      disabled?: boolean;
    }
  | {
      state:
        | ConditionCardActionState.RenewalInProgress
        | ConditionCardActionState.RenewalSubmitted;
    };

/** A condition the patient has requested but has never had a prescription for. */
export type NewConditionCardProps = ConditionCardBaseProps & {
  mode: ConditionCardMode.New;
  status: ConditionCardStatus;
  /** Overrides the copy the status would otherwise supply. */
  message?: string[];
};

/** An ongoing condition, showing the prescription the patient is on. */
export type OngoingPrescriptionCardProps = ConditionCardBaseProps & {
  mode: ConditionCardMode.OngoingPrescription;
  medications: string[];
  action?: ConditionCardAction;
  sentOn?: string | null;
  pharmacyName?: string | null;
  pharmacyPhone?: string | null;
  /** Advisory copy under the details, e.g. when to renew. */
  footnote?: string;
  callout?: { title: string; description: string };
  detailsPath?: string;
  onDetailsClick?: () => void;
};

/** An ongoing condition with something in flight instead of a prescription. */
export type OngoingStatusCardProps = ConditionCardBaseProps & {
  mode: ConditionCardMode.OngoingStatus;
  medications: string[];
  status:
    | ConditionCardActionState.RenewalInProgress
    | ConditionCardActionState.RenewalSubmitted;
  action?: ConditionCardAction;
  /** Overrides the copy the status would otherwise supply. */
  message?: string[];
  detailsPath?: string;
  onDetailsClick?: () => void;
};

export type ConditionCardProps =
  NewConditionCardProps | OngoingPrescriptionCardProps | OngoingStatusCardProps;

type TreatmentsKey = ParseKeys<'treatments'>;

const ACTION_LABELS = {
  startRenewal: 'ConditionCard.Actions.StartRenewal',
  updateStatus: 'ConditionCard.Actions.UpdateStatus',
  renewalInProgress: 'ConditionCard.Actions.RenewalInProgress',
  renewalSubmitted: 'ConditionCard.Actions.RenewalSubmitted',
} satisfies Record<ConditionCardActionState, TreatmentsKey>;

const MESSAGE_KEYS = {
  requestSubmitted: [
    'ConditionCard.Messages.RequestSubmitted.P1',
    'ConditionCard.Messages.RequestSubmitted.P2',
  ],
  renewalSubmitted: [
    'ConditionCard.Messages.RenewalSubmitted.P1',
    'ConditionCard.Messages.RenewalSubmitted.P2',
  ],
  renewalInProgress: ['ConditionCard.Messages.RenewalInProgress.P1'],
  startRenewal: [],
  updateStatus: [],
} satisfies Record<
  ConditionCardActionState | ConditionCardStatus,
  TreatmentsKey[]
>;

const useConditionCardTranslation = () => useTranslation('treatments');

const useMedicationList = (medications: string[]) => {
  const { i18n } = useTranslation();

  return new Intl.ListFormat(i18n.language, {
    style: 'long',
    type: 'conjunction',
  }).format(medications);
};

const contentColorFor = (variant: ConditionCardVariant) =>
  variant === 'primary' ? 'contentInverse' : 'contentDefault';

export const ConditionCard = (props: ConditionCardProps) => {
  switch (props.mode) {
    case ConditionCardMode.New:
      return <NewConditionCard {...props} />;
    case ConditionCardMode.OngoingPrescription:
      return <OngoingPrescriptionCard {...props} />;
    case ConditionCardMode.OngoingStatus:
      return <OngoingStatusCard {...props} />;
  }
};

const NewConditionCard = ({
  variant,
  title,
  icon,
  headingLevel,
  status,
  message,
}: NewConditionCardProps) => {
  const { t } = useConditionCardTranslation();

  return (
    <div className={clsx(card({ variant }), 'flex flex-col gap-m')}>
      <CardHeader
        variant={variant}
        title={title}
        icon={icon}
        headingLevel={headingLevel}
        cornerSlot={
          <Tag intent="accent" leadingIcon={<Icon name="check" size="small" />}>
            {t('ConditionCard.Statuses.RequestSubmitted')}
          </Tag>
        }
      />
      <Message
        variant={variant}
        paragraphs={message ?? defaultMessage(status, t)}
      />
    </div>
  );
};

const OngoingPrescriptionCard = ({
  variant,
  title,
  icon,
  headingLevel,
  medications,
  action,
  sentOn,
  pharmacyName,
  pharmacyPhone,
  footnote,
  callout,
  detailsPath,
  onDetailsClick,
}: OngoingPrescriptionCardProps) => {
  const { t } = useConditionCardTranslation();
  const isPrimary = variant === 'primary';

  return (
    <div className={cardWrapper({ variant })}>
      <CardNavigationRegion
        detailsPath={detailsPath}
        onDetailsClick={onDetailsClick}
      >
        <CardHeader
          variant={variant}
          title={title}
          icon={icon}
          headingLevel={headingLevel}
          medications={medications}
          cornerSlot={<DetailsChevron variant={variant} show={!!detailsPath} />}
        />
        {/* The `tel:` anchor inside the list has to stay above the card's link
            overlay, or tapping the phone number would open the details page.
            `stopPropagation()` would not do: it cannot un-nest the anchors. */}
        <div className="relative z-10">
          <DetailList
            tone={isPrimary ? 'accent' : 'neutral'}
            rows={[
              sentOn && {
                label: t('ConditionCard.PrescriptionSent'),
                value: sentOn,
              },
              pharmacyName && {
                label: t('ConditionCard.Pharmacy'),
                value: pharmacyName,
              },
              pharmacyPhone && {
                label: t('ConditionCard.PharmacyPhoneNumber'),
                value: pharmacyPhone,
                href: `tel:${pharmacyPhone}`,
              },
            ]}
          />
        </div>
        {callout && <Callout {...callout} />}
      </CardNavigationRegion>

      <div className="space-y-xs">
        <ActionButton variant={variant} action={action} />
        <Footnote variant={variant} footnote={footnote} />
      </div>
    </div>
  );
};

const OngoingStatusCard = ({
  variant,
  title,
  icon,
  headingLevel,
  medications,
  status,
  action,
  message,
  detailsPath,
  onDetailsClick,
}: OngoingStatusCardProps) => {
  const { t } = useConditionCardTranslation();

  return (
    <div className={cardWrapper({ variant })}>
      <CardNavigationRegion
        detailsPath={detailsPath}
        onDetailsClick={onDetailsClick}
      >
        <CardHeader
          variant={variant}
          title={title}
          icon={icon}
          headingLevel={headingLevel}
          medications={medications}
          cornerSlot={<DetailsChevron variant={variant} show={!!detailsPath} />}
        />
        <Message
          variant={variant}
          paragraphs={message ?? defaultMessage(status, t)}
        />
      </CardNavigationRegion>

      <div className="space-y-xs">
        <ActionButton variant={variant} action={action} />
        <Footnote variant={variant} />
      </div>
    </div>
  );
};

const defaultMessage = (
  key: ConditionCardActionState | ConditionCardStatus,
  t: (key: TreatmentsKey) => string,
) => MESSAGE_KEYS[key].map((messageKey) => t(messageKey));

/**
 * Everything above the CTA navigates to the details page. The link is an
 * absolutely positioned sibling rather than a wrapper, because `DetailList`
 * renders its own anchor for the pharmacy phone number and anchors cannot nest.
 */
const CardNavigationRegion = ({
  detailsPath,
  onDetailsClick,
  children,
}: {
  detailsPath?: string;
  onDetailsClick?: () => void;
  children: ReactNode;
}) => {
  const { t } = useConditionCardTranslation();

  return (
    <div className="relative space-y-m pb-m">
      {children}
      {detailsPath && (
        <Link
          to={detailsPath}
          onClick={onDetailsClick}
          data-card-link
          aria-label={t('ConditionCard.ViewDetails')}
          className="rounded-m absolute inset-0"
        />
      )}
    </div>
  );
};

const DetailsChevron = ({
  variant,
  show,
}: {
  variant: ConditionCardVariant;
  show: boolean;
}) =>
  show ? (
    <Icon
      name="chevron_right"
      size="medium"
      color={
        variant === 'primary' ? 'text-content-inverse' : 'text-content-default'
      }
    />
  ) : null;

const CardHeader = ({
  variant,
  title,
  icon,
  headingLevel = 'h2',
  medications,
  cornerSlot,
}: ConditionCardBaseProps & {
  medications?: string[];
  cornerSlot?: ReactNode;
}) => {
  const medicationList = useMedicationList(medications ?? []);

  return (
    <div className="space-y-xs">
      <div className="flex items-center justify-between gap-s">
        {icon}
        {cornerSlot}
      </div>
      <div className="space-y-xxs">
        <Text
          as={headingLevel}
          size="headingS"
          color={contentColorFor(variant)}
        >
          {title}
        </Text>
        {medicationList && (
          <Text size="label" color={contentColorFor(variant)}>
            {medicationList}
          </Text>
        )}
      </div>
    </div>
  );
};

const Message = ({
  variant,
  paragraphs,
}: {
  variant: ConditionCardVariant;
  paragraphs: string[];
}) => (
  <div className="space-y-xs">
    {paragraphs.map((paragraph) => (
      <Text key={paragraph} size="bodyS" color={contentColorFor(variant)}>
        {paragraph}
      </Text>
    ))}
  </div>
);

const ACTION_ICONS: Record<ConditionCardActionState, ReactNode> = {
  startRenewal: <Icon name="arrow_forward" size="small" />,
  updateStatus: <Icon name="arrow_forward" size="small" />,
  renewalSubmitted: <Icon name="check" size="small" />,
  renewalInProgress: null,
};

const ActionButton = ({
  variant,
  action,
}: {
  variant: ConditionCardVariant;
  action?: ConditionCardAction;
}) => {
  const { t } = useConditionCardTranslation();

  if (!action) return null;

  const isPending = !('onAction' in action);

  return (
    <Button
      size="small"
      width="full"
      intent={variant === 'primary' ? 'primary' : 'secondary'}
      trailingIcon={ACTION_ICONS[action.state]}
      disabled={isPending || action.disabled}
      onClick={isPending ? undefined : action.onAction}
    >
      {t(ACTION_LABELS[action.state])}
    </Button>
  );
};

const Callout = ({
  title,
  description,
}: NonNullable<OngoingPrescriptionCardProps['callout']>) => (
  <div className="rounded-m bg-warning space-y-xxs p-m">
    <Text size="labelStrong" color="contentInverse">
      {title}
    </Text>
    <Text size="bodyS" color="contentInverse">
      {description}
    </Text>
  </div>
);

const Footnote = ({
  variant,
  footnote,
}: {
  variant: ConditionCardVariant;
  footnote?: string;
}) => (
  <Text
    size="label"
    color={contentColorFor(variant)}
    className="block text-center"
    aria-hidden={footnote ? undefined : true}
  >
    {footnote ?? '\u00a0'}
  </Text>
);
